import { usePersistentState } from '@cld/use-persistency';
import { useCallback, useEffect } from 'react';
import { modules } from './side-menu.constants';
import { useTrackEvent } from '../../hooks/analytics.hooks';

export type ExpandedSectionsMap = Record<keyof typeof sectionNameActiveUrlsMap, boolean>;

export const sectionNameActiveUrlsMap = {
  reports: [modules.deliveryReports, modules.errorReports],
  manageAndAnalyze: [modules.mediaExplorer],
  transformAndCustomize: [modules.transformationsCenter, modules.transformationsManage, modules.transformationBuilder, modules.transformationLog],
  optimizeAndDeliver: [modules.optimizationCenter],
  video: [modules.videoHub, modules.videoPlayerReports],
};

const sectionNameAnalyticsEvenMap: Record<keyof typeof sectionNameActiveUrlsMap, string> = {
  manageAndAnalyze: 'manage and analyze',
  optimizeAndDeliver: 'optimize and deliver',
  reports: 'reports',
  transformAndCustomize: 'transform and customize',
  video: 'video',
};

export const useExpandedSectionsMap = (
  {
    defaultValue,
    storageKey,
    pathname,
  }: {
    defaultValue: ExpandedSectionsMap;
    storageKey: string;
    pathname: string;
  },
  trackEvent: ReturnType<typeof useTrackEvent>
) => {
  const [expandedSectionsMap, setExpandedSectionsMap] = usePersistentState(defaultValue, storageKey);
  const setExpandedSection = useCallback(
    (name: keyof typeof expandedSectionsMap, isExpanded: boolean) =>
      setExpandedSectionsMap((prevState) => {
        if (isExpanded !== prevState[name]) {
          trackEvent(sectionNameAnalyticsEvenMap[name]);
        }

        return {
          ...prevState,
          [name]: isExpanded,
        };
      }),
    [setExpandedSectionsMap, trackEvent]
  );

  useEffect(() => {
    const activeSectionName = (Object.entries(sectionNameActiveUrlsMap) as Array<[keyof typeof sectionNameActiveUrlsMap, string[]]>).find(([, urls]) =>
      urls.some((url) => pathname.endsWith(url))
    )?.[0];

    if (activeSectionName && !expandedSectionsMap[activeSectionName]) {
      setExpandedSection(activeSectionName, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [expandedSectionsMap, setExpandedSection] as const;
};
